import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authService } from './authService';

// Thunks
export const loginUser = createAsyncThunk('auth/login', async ({ username, password }, { rejectWithValue }) => {
  try {
    return await authService.login(username, password);
  } catch (error) {
    const message = error.message || 'Login failed';
    if (process.env.NODE_ENV === 'development') console.warn('[Auth] Login error:', error);
    return rejectWithValue(message);
  }
});

export const registerUser = createAsyncThunk('auth/register', async (userData, { rejectWithValue }) => {
  try {
    return await authService.register(userData);
  } catch (error) {
    let message = error.message || 'Registration failed';
    if (process.env.NODE_ENV === 'development') console.warn('[Auth] Registration error:', error);
    return rejectWithValue(message);
  }
});

export const mfaLoginVerify = createAsyncThunk('auth/mfaLoginVerify', async (code, { rejectWithValue }) => {
  try {
    return await authService.mfaLoginVerify(code);
  } catch (error) {
    return rejectWithValue(error.message || 'Invalid or expired code');
  }
});

export const logoutUser = createAsyncThunk('auth/logout', async (_, { rejectWithValue }) => {
  try {
    await authService.logout();
    return true;
  } catch (error) {
    console.warn('[Auth] Logout error:', error);
    return true; // always clear state on client
  }
});

export const refreshUserToken = createAsyncThunk('auth/refresh', async (_, { rejectWithValue }) => {
  try {
    return await authService.refreshToken();
  } catch (error) {
    const message = error.message || 'Token refresh failed';
    return rejectWithValue(message);
  }
});

export const loadUserProfile = createAsyncThunk('auth/profile', async (_, { rejectWithValue }) => {
  try {
    return await authService.getUserProfile();
  } catch (error) {
    const message = error.message || 'Failed to load profile';
    return rejectWithValue(message);
  }
});

export const updateUserProfile = createAsyncThunk('auth/updateProfile', async (userData, { rejectWithValue }) => {
  try {
    return await authService.updateUserProfile(userData);
  } catch (error) {
    const message = error.message || 'Failed to update profile';
    return rejectWithValue(message);
  }
});

export const changeUserPassword = createAsyncThunk('auth/changePassword', async (passwordData, { rejectWithValue }) => {
  try {
    return await authService.changePassword(passwordData);
  } catch (error) {
    const message = error.message || 'Failed to change password';
    return rejectWithValue(message);
  }
});

// Initial state: no tokens, no expiry
const initialState = {
  user: null,
  isAuthenticated: false,
  loading: false,
  error: null,
  lastActivity: Date.now(),
  mfaRequired: false,
  mfaSetupRequired: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, { payload }) => {
      state.user = payload;
      state.isAuthenticated = !!payload;
      state.error = null;
      state.loading = false;
      state.lastActivity = Date.now();
    },
    clearAuth: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.error = null;
      state.loading = false;
    },
    setError: (state, { payload }) => {
      state.error = payload;
      state.loading = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    updateLastActivity: (state) => {
      state.lastActivity = Date.now();
    },
    setLoading: (state, { payload }) => {
      state.loading = payload;
    },
    clearMfaRequired: (state) => {
      state.mfaRequired = false;
    },
    clearMfaSetupRequired: (state) => {
      state.mfaSetupRequired = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, { payload }) => {
        if (payload?.mfaRequired) {
          // Not a failure and not a success yet -- the password check passed,
          // but the session isn't live until /mfa/login-verify succeeds too.
          // Leave user/isAuthenticated untouched so no route thinks this is
          // a completed login until that second step finishes.
          state.mfaRequired = true;
          state.loading = false;
          state.error = null;
          return;
        }
        state.user = payload?.user || null;
        state.isAuthenticated = !!payload.user;
        state.mfaRequired = false;
        state.mfaSetupRequired = !!payload?.mfaSetupRequired;
        state.loading = false; // ensure reset
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload  || 'Login failed';
      })
      .addCase(mfaLoginVerify.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(mfaLoginVerify.fulfilled, (state, { payload }) => {
        state.user = payload?.user || null;
        state.isAuthenticated = !!payload?.user;
        state.mfaRequired = false;
        state.loading = false;
        state.error = null;
      })
      .addCase(mfaLoginVerify.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload || 'Invalid or expired code';
      })
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, { payload }) => {
        state.user = payload.user;
        state.isAuthenticated = false; 
        state.loading = false;
        state.error = null;
      })
      .addCase(registerUser.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload || 'Registration failed';
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null; state.isAuthenticated = false;
        state.loading = false; state.error = null;
      })
      .addCase(refreshUserToken.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(refreshUserToken.fulfilled, (state, { payload }) => {
        state.user = payload?.user || state.user;
        state.isAuthenticated = !!state.user;
        state.loading = false;
        state.error = null;
      })
      .addCase(refreshUserToken.rejected, (state, { payload }) => {
        state.user = null; state.isAuthenticated = false;
        state.loading = false; 
        state.error = payload  || 'Token refresh failed';
      })
      .addCase(loadUserProfile.pending, (state) => {
        state.loading = true; 
        state.error = null;
      })
      .addCase(loadUserProfile.fulfilled, (state, { payload }) => {
        // A successfully loaded profile IS proof of an authenticated session
        // (the request only succeeds with valid cookies) -- this was the one
        // fulfilled-case in the whole slice that populated `user` without
        // also flipping `isAuthenticated`, which left PrivateRoute (gates on
        // isAuthenticated only) bouncing an already-logged-in user back to
        // /login whenever this thunk ran without loginUser having run first
        // (e.g. the OAuth callback page, which hydrates from cookies alone).
        state.user = payload;
        state.isAuthenticated = !!payload;
        state.loading = false;
      })
      .addCase(loadUserProfile.rejected, (state, { payload }) => {
        state.loading = false; state.error = payload;
      })
      .addCase(updateUserProfile.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, { payload }) => {
        state.user = payload;
        state.loading = false;
      })
      .addCase(updateUserProfile.rejected, (state, { payload }) => {
        state.loading = false; state.error = payload;
      })
      .addCase(changeUserPassword.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(changeUserPassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(changeUserPassword.rejected, (state, { payload }) => {
        state.loading = false; state.error = payload;
      });
  }
});

export const {
  setUser,
  clearAuth,
  setError,
  clearError,
  updateLastActivity,
  setLoading,
  clearMfaRequired,
} = authSlice.actions;

export default authSlice.reducer;
